/**
 * web/src/hooks/mutations/useUpdateNudgePlan.ts
 * Full-replace edit of a tracked email's nudge plan (P2 nudge substrate) —
 * shift a due date, cancel one entry, add an entry, cancel all. Invalidates
 * the broad speakers prefix (the hub chip + journey read the plan) plus the
 * broad tracked-emails prefix (the GateReview follow-up editor's source).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { updateNudgePlan } from '../../api/tracked-emails';
import type { NudgePlanEntry } from '../../api/types';
import { toastApiError } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

export interface UseUpdateNudgePlanVars {
  trackedEmailId: string;
  /** The COMPLETE replacement plan (sent entries must be included unchanged). */
  nudges: NudgePlanEntry[];
}

/**
 * Replaces a tracked email's nudge plan wholesale.
 *
 * @returns React Query mutation handle (variables: { trackedEmailId, nudges })
 * @sideEffects HTTP PATCH; invalidates speakers + tracked-emails caches
 * @idempotent yes — re-sending the same plan is a no-op rewrite (server 409s
 *             only when a `sent` entry was altered/removed)
 */
export function useUpdateNudgePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: UseUpdateNudgePlanVars) => updateNudgePlan(vars.trackedEmailId, vars.nudges),
    onSuccess: () => {
      toast.success('Follow-up plan updated');
    },
    onError: (err) => {
      toastApiError(err, 'Could not update the follow-up plan');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.speakers.root() });
      qc.invalidateQueries({ queryKey: qk.trackedEmails.root() });
    },
  });
}
